import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGUAGES, useLanguage } from "../context/LanguageContext";

export function LanguageSwitcher({ variant = "desktop" }) {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="nav-lang"
        aria-label="Change language"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 hover:text-[#C8A96A] transition-colors ${
          variant === "mobile" ? "text-xl" : ""
        }`}
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <Globe size={variant === "mobile" ? 20 : 18} />
        <span className={variant === "mobile" ? "text-sm tracking-[0.16em] uppercase" : "text-[11px] tracking-[0.16em] uppercase"}>
          {lang}
        </span>
        <ChevronDown
          size={variant === "mobile" ? 16 : 12}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            data-testid="lang-menu"
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute z-50 mt-3 min-w-[180px] bg-[#F4EDE2] text-[#0C111C] border border-[#0C111C]/10 shadow-[0_20px_50px_rgba(12,17,28,0.18)] py-1.5 ${
              variant === "mobile" ? "left-0" : "right-0"
            }`}
          >
            {LANGUAGES.map((l) => {
              const active = l.code === lang;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => {
                      setLang(l.code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                      active
                        ? "bg-[#C8A96A]/15 text-[#0C111C]"
                        : "hover:bg-[#C8A96A]/10"
                    }`}
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <span aria-hidden="true" className="text-base leading-none">
                      {l.flag}
                    </span>
                    <span className="flex-1">{l.label}</span>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#0C111C]/50">
                      {l.code}
                    </span>
                    {active && (
                      <Check size={14} className="text-[#C8A96A] shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
